import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { AssetTaskSelect, selectedAssetTaskId } from "@/components/tasks/asset-task-pick-dialog"
import { Button, Input } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import type { TaskAssetCandidate } from "@/types/tasks"

export type EntityNameEditDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  nameLabel: string
  initialName: string
  onSubmit: (name: string, taskId: number | null) => Promise<void>
  closeAriaLabel: string
  cancelLabel: string
  saveLabel: string
  showSaveSpinner?: boolean
  /** Потолок длины имени на сервере: без него форма молча отдавала бы обрезаемое значение. */
  maxLength?: number
  /** Поверх уже открытого диалога (SceneDetailDialog и т.п.). */
  nested?: boolean | 2 | 3
  /** Непривязанные задачи. Пустой список — поля нет, создаётся новая. */
  tasks?: TaskAssetCandidate[]
}

export function EntityNameEditDialog({
  open,
  onOpenChange,
  title,
  nameLabel,
  initialName,
  onSubmit,
  closeAriaLabel,
  cancelLabel,
  saveLabel,
  showSaveSpinner = true,
  maxLength,
  nested,
  tasks,
}: EntityNameEditDialogProps) {
  const [name, setName] = useState(initialName)
  const [taskId, setTaskId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setTaskId(null)
      setError(null)
    }
  }, [open, initialName])

  const handleSubmit = async () => {
    setError(null)
    setBusy(true)
    try {
      await onSubmit(name.trim(), selectedAssetTaskId(tasks ?? [], taskId))
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      closeAriaLabel={closeAriaLabel}
      size="sm"
      nested={nested}
      bodyClassName="space-y-3 p-5 pt-4"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button type="button" size="sm" onClick={handleSubmit} disabled={busy || !name.trim()}>
            {busy && showSaveSpinner ? <Loader2 className="h-3 w-3 animate-spin" /> : saveLabel}
          </Button>
        </>
      }
    >
      <label className="block text-xs text-ui-text-secondary">
        {nameLabel}
        <Input
          className="mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              if (!busy && name.trim()) void handleSubmit()
            }
          }}
          autoFocus
          required
          maxLength={maxLength}
        />
      </label>
      {tasks != null ? <AssetTaskSelect tasks={tasks} value={taskId} onChange={setTaskId} /> : null}
      {error ? <div className="rounded-lg bg-rose-500/10 p-2 text-xs text-ui-status-failed">{error}</div> : null}
    </WorkspaceDialog>
  )
}
